import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { productApi } from "../api/productApi";
import "./Products.css";

export default function Products() {
  const brandColor = "#002244";
  const { i18n } = useTranslation();
  const lang = (i18n?.language || "az").toLowerCase();

  // ✅ search
  const [q, setQ] = useState("");

  // ✅ load more
  const [visibleCount, setVisibleCount] = useState(8);

  // ✅ backend data
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // modal (UI kimi saxlayırıq)
  const [orderModal, setOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    note: "",
  });

  // ✅ 3 dil pick (name/short)
  const pick = (p, base) => {
    const suffix = lang.startsWith("en") ? "En" : lang.startsWith("ru") ? "Ru" : "Az";

    const camel = `${base}${suffix}`; // nameAz
    const pascal = `${base[0].toUpperCase() + base.slice(1)}${suffix}`; // NameAz ehtimal
    const camelAz = `${base}Az`;
    const pascalAz = `${base[0].toUpperCase() + base.slice(1)}Az`;

    return p?.[camel] ?? p?.[pascal] ?? p?.[camelAz] ?? p?.[pascalAz] ?? "";
  };

  const titleText = useMemo(() => {
    if (lang.startsWith("en")) return "Products";
    if (lang.startsWith("ru")) return "Продукты";
    return "Məhsullar";
  }, [lang]);

  const detailText = useMemo(() => {
    if (lang.startsWith("en")) return "Details →";
    if (lang.startsWith("ru")) return "Подробнее →";
    return "Ətraflı →";
  }, [lang]);

  const moreText = useMemo(() => {
    if (lang.startsWith("en")) return "Load more";
    if (lang.startsWith("ru")) return "Загрузить ещё";
    return "Daha çox göstər";
  }, [lang]);

  const searchPh = useMemo(() => {
    if (lang.startsWith("en")) return "Search...";
    if (lang.startsWith("ru")) return "Поиск...";
    return "Axtar...";
  }, [lang]);

  const notFoundText = useMemo(() => {
    if (lang.startsWith("en")) return "No product found.";
    if (lang.startsWith("ru")) return "Ничего не найдено.";
    return "Heç bir məhsul tapılmadı.";
  }, [lang]);

  // ✅ backenddən yüklə
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await productApi.getAll();
        const sorted = (res.data || [])
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setProducts(sorted);
      } catch (e) {
        setErr(e?.response?.data || e.message || "Load failed");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ✅ search filter (title + short + price)
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return products;

    return (products || []).filter((p) => {
      const text =
        [
          pick(p, "name"),
          pick(p, "short"),
          p?.price ?? "",
          String(p?.id ?? ""),
        ]
          .join(" ")
          .toLowerCase();

      return text.includes(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, products, i18n.language]);

  // modal funcs (UI)
  const openModal = (product) => {
    setSelectedProduct(product);
    setOrderModal(true);
  };

  const closeModal = () => {
    setOrderModal(false);
    setSelectedProduct(null);
    setForm({ name: "", surname: "", email: "", phone: "", note: "" });
  };

  const submitModal = () => {
    alert("Sifarişiniz uğurla qeydə alındı. Qısa zamanda sizinlə əlaqə saxlanılacaq.");
    closeModal();
  };

  return (
    <Layout>
      <section className="section productsPage" style={{ ["--brand"]: brandColor }}>
        <div className="container">
          {/* Başlıq */}
          <div className="sectionHead mb-3">
            <h1 className="hTitle mb-1">{titleText}</h1>
          </div>

          {/* ✅ SEARCH (Blog-dakı kimi) */}
          <div className="card prdSearchCard mb-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-12">
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setVisibleCount(8); // ✅ search dəyişəndə yenidən 8-dən başlasın
                  }}
                  className="form-control prdSearch"
                  placeholder={searchPh}
                />
              </div>
            </div>
          </div>

          {/* Loading / Error */}
          {loading ? (
            <div className="card">
              <div className="cardBody">Yüklənir...</div>
            </div>
          ) : err ? (
            <div className="card">
              <div className="cardBody" style={{ color: "rgba(255,77,79,.9)" }}>
                {String(err)}
              </div>
            </div>
          ) : (
            <>
              {/* Grid */}
              <div className="row g-3">
                {filtered.slice(0, visibleCount).map((p) => (
                  <div key={p.id} className="col-12 col-sm-6 col-lg-3">
                    <article className="prdCard h-100">
                      <div className="prdImgWrap">
                        {p.hasImage ? (
                          <img
                            className="prdImg"
                            src={productApi.getImageUrl(p.id) + `?t=${Date.now()}`}
                            alt={pick(p, "name")}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="prdImg prdImgFallback">No image</div>
                        )}

                        {/* {p.price ? <div className="prdPrice">{p.price}</div> : null} */}
                      </div>

                      <div className="prdBody">
                        <div className="prdName">{pick(p, "name")}</div>
                        <div className="prdDesc">{pick(p, "short")}</div>

                        <div className="prdActions">
                          <Link to={`/mehsullar/${p.id}`} className="btn btnBrand w-100">
                            {detailText}
                          </Link>

                          {/* istəyirsənsə aktiv edib orders API-yə bağlayarıq */}
                          {/* <button
                            type="button"
                            className="btn btnOutlineBrand w-100"
                            onClick={() => openModal(p)}
                          >
                            Sifariş et
                          </button> */}
                        </div>
                      </div>
                    </article>
                  </div>
                ))}

                {/* ✅ axtarışda heç nə yoxdursa */}
                {filtered.length === 0 ? (
                  <div className="col-12">
                    <div className="card">
                      <div className="cardBody" style={{ padding: 16 }}>
                        {notFoundText}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Load more */}
              {visibleCount < filtered.length && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="btn btnBrand px-4 py-3"
                    onClick={() => setVisibleCount((prev) => prev + 8)}
                  >
                    {moreText}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* MODAL (UI) */}
        {orderModal && (
          <div className="modalOverlay" onClick={closeModal}>
            <div className="modalBox" onClick={(e) => e.stopPropagation()}>
              <div className="modalHead">
                <h2 className="modalTitle">
                  Sifariş et — <span>{pick(selectedProduct, "name")}</span>
                </h2>

                <button className="modalX" onClick={closeModal} aria-label="Bağla">
                  ✕
                </button>
              </div>

              <div className="modalGrid">
                <input
                  className="modalInput"
                  placeholder="Ad"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />

                <input
                  className="modalInput"
                  placeholder="Soyad"
                  value={form.surname}
                  onChange={(e) => setForm({ ...form, surname: e.target.value })}
                />

                <input
                  className="modalInput"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />

                <input
                  className="modalInput"
                  placeholder="Telefon"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              <textarea
                className="modalInput modalTextarea"
                placeholder="Qeyd"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />

              <div className="modalActions">
                <button className="btn btnBrand w-100" onClick={submitModal}>
                  Göndər
                </button>
                <button className="btn btnOutlineBrand w-100" onClick={closeModal}>
                  Bağla
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
